/**
 * Pure helpers for the chart's axes and marks (kept apart from ECharts so they can be tested).
 */

/**
 * Decimals needed to write an axis tick exactly. Ticks are multiples of a "nice" interval
 * (0.05, 0.2, 25 …), so a tick needs as many decimals as it has — rounding every tick to one
 * decimal wrote 0.05, 0.1, 0.15 as "0.1, 0.1, 0.2". Float noise (0.30000000000000004) is ignored.
 */
export function tickDecimals(value: number, max = 6): number {
  if (!Number.isFinite(value) || value === 0) return 0;
  const text = String(Number(Math.abs(value).toPrecision(12)));
  const exp = /e-(\d+)$/.exec(text);
  if (exp) {
    const mantissa = text.slice(0, exp.index);
    const dot = mantissa.indexOf('.');
    return Math.min(max, Number(exp[1]) + (dot < 0 ? 0 : mantissa.length - dot - 1));
  }
  const dot = text.indexOf('.');
  return dot < 0 ? 0 : Math.min(max, text.length - dot - 1);
}

/**
 * Decimals for a value in a tooltip: one decimal from 1 up, none from 100 up, and two
 * significant digits below 1 (a cost of €0.004 is not "0").
 */
export function valueDecimals(value: number): number {
  const a = Math.abs(value);
  if (a === 0 || a >= 100) return 0;
  if (a >= 1) return 1;
  return Math.min(6, 1 - Math.floor(Math.log10(a)));
}

/**
 * Indexes of points drawn by nothing on a line: a value with a gap (null, or the series' end)
 * on both sides. A line needs two neighbouring values to draw a segment, so these get a dot.
 */
export function isolatedPoints(values: (number | null | undefined)[]): Set<number> {
  const out = new Set<number>();
  values.forEach((v, i) => {
    if (v == null) return;
    const before = i > 0 ? values[i - 1] : null;
    const after = i < values.length - 1 ? values[i + 1] : null;
    if (before == null && after == null) out.add(i);
  });
  return out;
}
